/** Biblioteca do aluno: títulos sempre visíveis, bloqueados quando não liberados. */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { isPalavras } from '@/services/palavrasBrand'

import { fetchLibrary } from '@/services/readerService'
import type { LibraryItem } from '@/types/api'
import { useAuthStore } from '@/stores/authStore'

interface LibraryViewProps {
  onOpen: (item: LibraryItem) => void
}

function BookCover({ item }: { item: LibraryItem }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  if (item.cover_url && item.cover_url !== failedUrl) {
    return <img className="library-cover library-cover--image" src={item.cover_url} alt="" loading="lazy" decoding="async" width="300" height="400" onError={() => setFailedUrl(item.cover_url ?? null)} />
  }
  return <span className="library-cover" aria-hidden="true"><small>LIVRO DIGITAL</small><strong>{item.title}</strong><small>{isPalavras() ? 'Palavras Educação' : 'Lúmina'} · {item.format.toUpperCase()}</small></span>
}

export function LibraryView({ onOpen }: LibraryViewProps) {
  const name = useAuthStore((s) => s.user?.name)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['library'],
    queryFn: fetchLibrary,
  })

  if (isLoading) return <p role="status">Carregando sua biblioteca…</p>
  if (isError) return <p role="alert">Não foi possível carregar a biblioteca.</p>
  const groups = data?.map((group) => ({ ...group, items: group.items.filter((item) => item.kind === 'book' && item.format === 'pdf') })).filter((group) => group.items.length)
  if (!groups?.length) {
    return <p>Você ainda não tem material disponível. Fale com seu professor.</p>
  }

  return (
    <div className="library">
      <h1>{name ? `Oi, ${name.split(' ')[0]}!` : 'Meus livros'}</h1>
      <p className="library-intro">Sua próxima descoberta começa com uma página.</p>
      {groups.map((grupo) => (
        <section key={grupo.collection.id}>
          <h2>{grupo.collection.title}</h2>
          <ul className="library-items">
            {grupo.items.map((item) => (
              <li key={item.asset_id}>
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  disabled={item.locked}
                  aria-label={
                    item.locked
                      ? `${item.title} — ainda não liberado`
                      : item.kind === 'audio'
                        ? `Ouvir ${item.title}`
                        : `Abrir ${item.title}`
                  }
                >
                  <BookCover item={item} />
                  <strong>{item.title}</strong>
                  <span className="library-format">
                    {item.kind === 'audio' ? 'ÁUDIO' : item.format.toUpperCase()}
                  </span>
                  {item.locked && (
                    <span className="library-locked">
                      {item.next_release_at
                        ? `Libera em ${new Date(item.next_release_at).toLocaleDateString('pt-BR')}`
                        : 'Ainda não liberado'}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
